import React, { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { PluginInterface, PluginConfigProps, PluginPreviewProps } from '../types/PluginInterface';
import AutoTranslateText from '../components/AutoTranslateText';
import PayPalOrdersPanel from '../components/PayPalOrdersPanel';
import StripeOrdersPanel from '../components/StripeOrdersPanel';
import { usePaymentApiKeys } from '../hooks/usePaymentApiKeys';
import { loadStripe } from '@stripe/stripe-js';
import { Elements } from '@stripe/react-stripe-js';
import StripePaymentElementForm from '../components/StripePaymentElementForm';
import { PayPalScriptProvider } from '@paypal/react-paypal-js';
import PayPalCheckoutButtons from '../components/PayPalCheckoutButtons';
import { generateUniformPlaceholder } from '../utils/PlaceholderGenerator';
import { api } from '../services/api';

type Provider = 'stripe' | 'paypal' | 'link';
type Mode = 'payment' | 'subscription';

export interface PaymentButtonsConfig {
  id: string; // ✅ ID unique
  ownerId?: string | number;
  title: string;
  description?: string;

  // Action
  provider: Provider;
  mode: Mode;

  // Stripe (Elements) / PayPal (SDK)
  amount?: number;            // en unité monétaire (ex: 12.99)
  currency?: string;          // EUR, USD...
  planId?: string;            // pour abonnement (si applicable)
  successUrl?: string;
  cancelUrl?: string;

  // Mode lien (Payment Link Stripe / bouton PayPal hébergé)
  linkUrl?: string;           // URL cible (paiement unique ou abonnement)

  // Style du bouton
  label?: string;
  icon?: string;              // emoji/texte avant label
  bgColor?: string;
  textColor?: string;
  borderColor?: string;
  borderRadius?: number;      // px
  fontFamily?: string;
  fontSize?: number;          // px
  fontWeight?: number;        // 400, 600, 700...
  paddingV?: number;          // px
  paddingH?: number;          // px
  width?: number;             // px ou undefined
  customClassName?: string;

  // Attributs personnalisés (data-*)
  attributes?: Array<{ name: string; value: string }>;

  // Comportement
  openIn?: 'modal' | 'newtab' | 'same'; // pour provider=link

  // Références aux clés API utilisateur (ID dans la table ApiKey)
  stripeKeyId?: number;
  paypalKeyId?: number;
}

const defaultConfig: PaymentButtonsConfig = {
  id: 'payment-buttons',
  title: 'Bouton de paiement',
  description: 'Bouton de paiement/abonnement configurable',
  provider: 'stripe',
  mode: 'payment',
  amount: 9.99,
  currency: 'EUR',
  label: 'Payer maintenant',
  icon: '💳',
  bgColor: '#0d6efd',
  textColor: '#ffffff',
  borderColor: '#0d6efd',
  borderRadius: 10,
  fontFamily: "'Segoe UI', Roboto, Arial, sans-serif",
  fontSize: 15,
  fontWeight: 600,
  paddingV: 10,
  paddingH: 14,
  width: undefined,
  customClassName: '',
  attributes: [],
  openIn: 'modal',
};

const PaymentButtonsConfigComponent: React.FC<PluginConfigProps> = ({ config, onConfigChange }) => {
  const current = { ...defaultConfig, ...(config as PaymentButtonsConfig) };
  const { hasPaypalKey, hasStripeKey } = usePaymentApiKeys();

  const update = (u: Partial<PaymentButtonsConfig>) => onConfigChange({ ...current, ...u });

  useEffect(() => {
    const storedUser = localStorage.getItem('user');
    if (!storedUser) return;
    const cfg = config as PaymentButtonsConfig;
    if (cfg.ownerId || (cfg as any).userId) return;
    try {
      const userData = JSON.parse(storedUser);
      const ownerId = userData?.id || userData?.userId;
      if (ownerId) {
        onConfigChange({ ...config, ownerId: String(ownerId) });
      }
    } catch {
      // ignore
    }
  }, [config, onConfigChange]);

  return (
    <div className="container-fluid p-0">
      <div className="row g-3">
        <div className="col-md-6">
          <label className="form-label"><AutoTranslateText sourceLang="fr">Fournisseur</AutoTranslateText></label>
          <select className="form-select" value={current.provider} onChange={e => update({ provider: e.target.value as Provider })}>
            <option value="stripe">Stripe</option>
            <option value="paypal">PayPal</option>
            <option value="link">Lien (Payment Link / PayPal hébergé)</option>
          </select>
        </div>
        <div className="col-md-6">
          <label className="form-label"><AutoTranslateText sourceLang="fr">Mode</AutoTranslateText></label>
          <select className="form-select" value={current.mode} onChange={e => update({ mode: e.target.value as Mode })}>
            <option value="payment">Paiement unique</option>
            <option value="subscription">Abonnement</option>
          </select>
        </div>

        {current.provider === 'link' ? (
          <div className="col-12">
            <label className="form-label"><AutoTranslateText sourceLang="fr">URL de paiement/abonnement</AutoTranslateText></label>
            <input className="form-control" value={current.linkUrl || ''} onChange={e => update({ linkUrl: e.target.value })} placeholder="https://buy.stripe.com/... ou https://www.paypal.com/ncp/payment/..." />
            <div className="mt-2">
              <label className="form-label"><AutoTranslateText sourceLang="fr">Ouverture</AutoTranslateText></label>
              <select className="form-select" value={current.openIn} onChange={e => update({ openIn: e.target.value as any })}>
                <option value="modal">Modale (si géré en interne)</option>
                <option value="newtab">Nouvel onglet</option>
                <option value="same">Même onglet</option>
              </select>
            </div>
          </div>
        ) : (
          <>
            {current.mode === 'payment' && (
              <>
                <div className="col-md-6">
                  <label className="form-label"><AutoTranslateText sourceLang="fr">Montant</AutoTranslateText></label>
                  <input type="number" step="0.01" className="form-control" value={current.amount ?? 0} onChange={e => update({ amount: parseFloat(e.target.value) || 0 })} />
                </div>
                <div className="col-md-6">
                  <label className="form-label"><AutoTranslateText sourceLang="fr">Devise</AutoTranslateText></label>
                  <select className="form-select" value={current.currency} onChange={e => update({ currency: e.target.value })}>
                    <option value="EUR">EUR</option>
                    <option value="USD">USD</option>
                    <option value="GBP">GBP</option>
                  </select>
                </div>
              </>
            )}
            {current.mode === 'subscription' && (
              <div className="col-12">
                <label className="form-label"><AutoTranslateText sourceLang="fr">Identifiant de plan (abonnement)</AutoTranslateText></label>
                <input className="form-control" value={current.planId || ''} onChange={e => update({ planId: e.target.value })} placeholder="ex: price_XXX (Stripe) ou Plan ID PayPal" />
              </div>
            )}
          </>
        )}

        <div className="col-12"><hr /></div>

        <div className="col-md-6">
          <label className="form-label"><AutoTranslateText sourceLang="fr">Libellé</AutoTranslateText></label>
          <input className="form-control" value={current.label || ''} onChange={e => update({ label: e.target.value })} />
          <div className="form-text"><AutoTranslateText sourceLang="fr">Texte du bouton</AutoTranslateText></div>
        </div>
        <div className="col-md-6">
          <label className="form-label"><AutoTranslateText sourceLang="fr">Icône (facultatif)</AutoTranslateText></label>
          <input className="form-control" value={current.icon || ''} onChange={e => update({ icon: e.target.value })} placeholder="ex: 💳, 🅿️, ⭐" />
        </div>

        <div className="col-md-4">
          <label className="form-label">Background</label>
          <input type="color" className="form-control form-control-color" value={current.bgColor || '#0d6efd'} onChange={e => update({ bgColor: e.target.value })} />
        </div>
        <div className="col-md-4">
          <label className="form-label">Texte</label>
          <input type="color" className="form-control form-control-color" value={current.textColor || '#ffffff'} onChange={e => update({ textColor: e.target.value })} />
        </div>
        <div className="col-md-4">
          <label className="form-label">Bordure</label>
          <input type="color" className="form-control form-control-color" value={current.borderColor || '#0d6efd'} onChange={e => update({ borderColor: e.target.value })} />
        </div>

        <div className="col-md-6">
          <label className="form-label">Police</label>
          <input className="form-control" value={current.fontFamily || ''} onChange={e => update({ fontFamily: e.target.value })} />
        </div>
        <div className="col-md-6">
          <label className="form-label">Taille (px)</label>
          <input type="number" className="form-control" value={current.fontSize || 15} onChange={e => update({ fontSize: parseInt(e.target.value) || 15 })} />
        </div>

        <div className="col-md-4">
          <label className="form-label">Graisse</label>
          <input type="number" className="form-control" min={100} max={900} step={100} value={current.fontWeight || 600} onChange={e => update({ fontWeight: parseInt(e.target.value) || 600 })} />
        </div>
        <div className="col-md-4">
          <label className="form-label">Rayon (px)</label>
          <input type="number" className="form-control" value={current.borderRadius || 10} onChange={e => update({ borderRadius: parseInt(e.target.value) || 10 })} />
        </div>
        <div className="col-md-4">
          <label className="form-label">Largeur (px, optionnel)</label>
          <input type="number" className="form-control" value={current.width || 0} onChange={e => update({ width: parseInt(e.target.value) || undefined })} />
        </div>

        <div className="col-md-6">
          <label className="form-label">Padding vertical (px)</label>
          <input type="number" className="form-control" value={current.paddingV || 10} onChange={e => update({ paddingV: parseInt(e.target.value) || 10 })} />
        </div>
        <div className="col-md-6">
          <label className="form-label">Padding horizontal (px)</label>
          <input type="number" className="form-control" value={current.paddingH || 14} onChange={e => update({ paddingH: parseInt(e.target.value) || 14 })} />
        </div>

        <div className="col-12">
          <label className="form-label"><AutoTranslateText sourceLang="fr">Attributs personnalisés data-*</AutoTranslateText></label>
          <div className="d-flex flex-column gap-2">
            {(current.attributes || []).map((attr, idx) => (
              <div className="d-flex gap-2" key={idx}>
                <input className="form-control" placeholder="data-tracking" value={attr.name} onChange={e => {
                  const next = [...(current.attributes || [])];
                  next[idx] = { ...next[idx], name: e.target.value };
                  update({ attributes: next });
                }} />
                <input className="form-control" placeholder="value" value={attr.value} onChange={e => {
                  const next = [...(current.attributes || [])];
                  next[idx] = { ...next[idx], value: e.target.value };
                  update({ attributes: next });
                }} />
                <button className="btn btn-outline-danger" onClick={() => {
                  const next = [...(current.attributes || [])];
                  next.splice(idx, 1);
                  update({ attributes: next });
                }}>🗑️</button>
              </div>
            ))}
            <button className="btn btn-outline-secondary btn-sm" onClick={() => update({ attributes: [...(current.attributes || []), { name: 'data-info', value: 'x' }] })}>
              ➕ <AutoTranslateText sourceLang="fr">Ajouter un attribut</AutoTranslateText>
            </button>
          </div>
        </div>
      </div>

      {hasPaypalKey && (
        <>
          <hr className="my-4" />
          <h6 className="fw-bold mb-3">🅿 Transactions PayPal</h6>
          <PayPalOrdersPanel pluginType="payment-button" shopId={current.id} />
        </>
      )}
      {hasStripeKey && (
        <>
          <hr className="my-4" />
          <h6 className="fw-bold mb-3">💜 Transactions Stripe</h6>
          <StripeOrdersPanel pluginType="payment-button" shopId={current.id} />
        </>
      )}
    </div>
  );
};

const PaymentButtonsPreview: React.FC<PluginPreviewProps> = ({ config }) => {
  const cfg = { ...defaultConfig, ...(config as PaymentButtonsConfig) };
  const { stripePublicKey, paypalClientId } = usePaymentApiKeys();
  const resolvedOwnerId =
    cfg.ownerId ||
    (cfg as any).userId ||
    (cfg as any).createdBy ||
    (cfg as any).created_by ||
    (cfg as any).authorId;
  const stripePromise = useMemo(() => (stripePublicKey ? loadStripe(stripePublicKey) : null), [stripePublicKey]);

  const [openModal, setOpenModal] = useState(false);
  const [stripeClientSecret, setStripeClientSecret] = useState<string | null>(null);
  const [stripeBusy, setStripeBusy] = useState(false);
  const [stripeError, setStripeError] = useState<string | null>(null);
  const [stripeSuccess, setStripeSuccess] = useState<string | null>(null);
  const [subscriptionId, setSubscriptionId] = useState<string | null>(null);
  const stripeElementsOptions = useMemo(() => {
    if (!stripeClientSecret) return undefined;
    return { clientSecret: stripeClientSecret, appearance: { theme: 'stripe' as const } };
  }, [stripeClientSecret]);

  useEffect(() => {
    if (!openModal && stripeClientSecret) {
      setStripeClientSecret(null);
      setStripeError(null);
      setStripeSuccess(null);
      setSubscriptionId(null);
    }
  }, [openModal, stripeClientSecret]);

  const btnStyle: React.CSSProperties = {
    background: cfg.bgColor,
    color: cfg.textColor,
    borderColor: cfg.borderColor,
    borderWidth: 1,
    borderStyle: 'solid',
    borderRadius: cfg.borderRadius,
    fontFamily: cfg.fontFamily,
    fontSize: cfg.fontSize,
    fontWeight: cfg.fontWeight as any,
    padding: `${cfg.paddingV}px ${cfg.paddingH}px`,
    width: cfg.width ? `${cfg.width}px` : undefined,
    display: 'inline-flex',
    alignItems: 'center',
    gap: 8,
    cursor: 'pointer'
  };

  const commonAttrs = (el: React.ButtonHTMLAttributes<HTMLButtonElement>) => {
    const out: any = { ...el };
    (cfg.attributes || []).forEach(attr => {
      if (attr?.name) out[attr.name] = attr.value ?? '';
    });
    return out;
  };

  const handleClick = () => {
    // Lien externe
    if (cfg.provider === 'link') {
      if (!cfg.linkUrl) return;
      if (cfg.openIn === 'newtab') window.open(cfg.linkUrl, '_blank', 'noopener,noreferrer');
      else if (cfg.openIn === 'same') window.location.href = cfg.linkUrl;
      else setOpenModal(true);
      return;
    }
    // SDK en modale
    setOpenModal(true);
  };

  // Avertissements clés manquantes
  const missingStripe = cfg.provider === 'stripe' && (!stripePublicKey || !resolvedOwnerId);
  const missingPaypal = cfg.provider === 'paypal' && !paypalClientId;

  return (
    <div>
      {(missingStripe || missingPaypal) && (
        <div className="alert alert-warning py-2">
          {missingStripe && <span><AutoTranslateText sourceLang="fr">Clé Stripe manquante.</AutoTranslateText> </span>}
          {missingPaypal && <span><AutoTranslateText sourceLang="fr">Clé PayPal manquante.</AutoTranslateText></span>}
        </div>
      )}
      <button
        type="button"
        className={`btn ${cfg.customClassName || ''}`}
        style={btnStyle}
        onClick={handleClick}
        {...commonAttrs({})}
        title={cfg.label}
        aria-label={cfg.label}
      >
        {cfg.icon ? <span aria-hidden="true">{cfg.icon}</span> : null}
        <span><AutoTranslateText sourceLang="fr">{cfg.label || 'Payer'}</AutoTranslateText></span>
      </button>

      {openModal && createPortal(
        <div className="pb-modal-backdrop" onClick={() => setOpenModal(false)}>
          <div className="pb-modal-container" onClick={(e) => e.stopPropagation()}>
            <div className="pb-modal-header">
              <strong>
                {cfg.icon ? <span style={{ marginRight: 6 }}>{cfg.icon}</span> : null}
                <AutoTranslateText sourceLang="fr">
                  {cfg.mode === 'subscription' ? 'Abonnement' : 'Paiement'}
                </AutoTranslateText>
              </strong>
              <button className="btn btn-sm btn-light" onClick={() => setOpenModal(false)}>✕</button>
            </div>
            <div className="pb-modal-body">
              {cfg.provider === 'link' && cfg.linkUrl && cfg.openIn === 'modal' && (
                <iframe
                  title="Paiement"
                  src={cfg.linkUrl}
                  style={{ width: '100%', height: '70vh', border: 0 }}
                />
              )}

              {cfg.provider === 'stripe' && stripePromise && cfg.mode === 'payment' && (
                <>
                  {!stripeClientSecret && (
                    <button
                      className="btn btn-primary w-100"
                      onClick={async () => {
                        if (!resolvedOwnerId) {
                          setStripeError('Propriétaire du plugin non défini.');
                          return;
                        }
                        if (!cfg.amount || cfg.amount <= 0) {
                          setStripeError('Montant invalide.');
                          return;
                        }
                        setStripeBusy(true);
                        setStripeError(null);
                        try {
                          const resp: any = await api.post('/payment-buttons/stripe-intent', {
                            ownerId: resolvedOwnerId,
                            amount: cfg.amount,
                            currency: cfg.currency || 'EUR',
                            title: cfg.title,
                            description: cfg.description
                          });
                          if (!resp?.clientSecret) throw new Error(resp?.message || 'Client secret manquant.');
                          setStripeClientSecret(resp.clientSecret);
                          setStripeSuccess(null);
                        } catch (err: any) {
                          setStripeError(err?.message || 'Erreur Stripe.');
                        } finally {
                          setStripeBusy(false);
                        }
                      }}
                      disabled={stripeBusy}
                    >
                      {stripeBusy ? 'Préparation...' : 'Continuer avec Stripe'}
                    </button>
                  )}
                  {stripeError && <div className="alert alert-danger mt-2">{stripeError}</div>}
                  {stripeClientSecret && stripeElementsOptions && (
                    <Elements stripe={stripePromise} options={stripeElementsOptions}>
                      <StripePaymentElementForm
                        onSuccess={async (paymentIntentId) => {
                          try {
                            const resp: any = await api.post('/payment-buttons/stripe-confirm-intent', {
                              ownerId: resolvedOwnerId,
                              paymentIntentId
                            });
                            if (!resp?.success) throw new Error(resp?.message || 'Paiement non confirmé.');
                            setStripeSuccess('✅ Paiement validé');
                            setStripeClientSecret(null);
                          } catch (err: any) {
                            setStripeError(err?.message || 'Erreur de confirmation Stripe.');
                          }
                        }}
                        onError={(err) => setStripeError(String(err || 'Erreur Stripe'))}
                        submitLabel={cfg.label || 'Payer'}
                      />
                    </Elements>
                  )}
                  {stripeSuccess && <div className="alert alert-success mt-3">{stripeSuccess}</div>}
                </>
              )}

              {cfg.provider === 'stripe' && stripePromise && cfg.mode === 'subscription' && (
                <>
                  {!stripeClientSecret && (
                    <button
                      className="btn btn-primary w-100"
                      onClick={async () => {
                        if (!resolvedOwnerId) {
                          setStripeError('Propriétaire du plugin non défini.');
                          return;
                        }
                        if (!cfg.planId) {
                          setStripeError('Plan Stripe (price) manquant.');
                          return;
                        }
                        setStripeBusy(true);
                        setStripeError(null);
                        try {
                          const resp: any = await api.post('/payment-buttons/stripe-subscription-intent', {
                            ownerId: resolvedOwnerId,
                            priceId: cfg.planId,
                            title: cfg.title,
                            description: cfg.description
                          });
                          if (!resp?.clientSecret) throw new Error(resp?.message || 'Client secret manquant.');
                          setStripeClientSecret(resp.clientSecret);
                          setSubscriptionId(resp?.subscriptionId || null);
                          setStripeSuccess(null);
                        } catch (err: any) {
                          setStripeError(err?.message || 'Erreur Stripe.');
                        } finally {
                          setStripeBusy(false);
                        }
                      }}
                      disabled={stripeBusy}
                    >
                      {stripeBusy ? 'Préparation...' : 'Continuer avec Stripe'}
                    </button>
                  )}
                  {stripeError && <div className="alert alert-danger mt-2">{stripeError}</div>}
                  {stripeClientSecret && stripeElementsOptions && (
                    <Elements stripe={stripePromise} options={stripeElementsOptions}>
                      <StripePaymentElementForm
                        onSuccess={async (paymentIntentId) => {
                          try {
                            const resp: any = await api.post('/payment-buttons/stripe-confirm-intent', {
                              ownerId: resolvedOwnerId,
                              paymentIntentId
                            });
                            if (!resp?.success) throw new Error(resp?.message || 'Paiement non confirmé.');
                            setStripeSuccess('✅ Abonnement activé');
                            setStripeClientSecret(null);
                          } catch (err: any) {
                            setStripeError(err?.message || 'Erreur de confirmation Stripe.');
                          }
                        }}
                        onError={(err) => setStripeError(String(err || 'Erreur Stripe'))}
                        submitLabel={cfg.label || 'S’abonner'}
                      />
                    </Elements>
                  )}
                  {stripeSuccess && (
                    <div className="alert alert-success mt-3">
                      {stripeSuccess}
                      {subscriptionId ? <div className="small text-muted mt-1">ID abonnement: {subscriptionId}</div> : null}
                    </div>
                  )}
                </>
              )}

              {cfg.provider === 'paypal' && paypalClientId && cfg.mode === 'payment' && (
                <PayPalScriptProvider options={{ clientId: paypalClientId, currency: cfg.currency || 'EUR' }}>
                  <PayPalCheckoutButtons
                    amount={(cfg.amount ?? 0).toFixed(2)}
                    currency={cfg.currency || 'EUR'}
                    onSuccess={(details) => {
                      console.log('PayPal success', details);
                      setOpenModal(false);
                    }}
                    onError={(err) => console.error('PayPal error', err)}
                  />
                </PayPalScriptProvider>
              )}

              {(cfg.mode === 'subscription') && (
                <div className="alert alert-info">
                  <AutoTranslateText sourceLang="fr">
                    Pour les abonnements, renseignez un plan (planId) et routez via un lien hébergé (provider=link) ou implémentez votre logique serveur.
                  </AutoTranslateText>
                </div>
              )}

              {((cfg.provider === 'stripe' && (!stripePublicKey || !resolvedOwnerId)) || (cfg.provider === 'paypal' && !paypalClientId)) && (
                <div className="text-muted small">
                  <AutoTranslateText sourceLang="fr">
                    Configurez vos clés API dans votre profil pour activer ce mode.
                  </AutoTranslateText>
                </div>
              )}
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
};

const PaymentButtonsModular: PluginInterface = {
  metadata: {
    id: 'payment-buttons-modular',
    name: 'Bouton de Paiement',
    version: '1.0.0',
    description: 'Boutons personnalisables pour Stripe/PayPal (paiement/abonnement)',
    author: 'Book Editor',
    icon: '💳',
    category: 'commerce',
    tags: ['paiement', 'stripe', 'paypal', 'subscription', 'payment']
  },

  // ✅ AJOUT DE LA DOCUMENTATION POUR L'IA (Nouveau Standard)
  // ⚠️ Checklist: mettre à jour documentationForAI à chaque évolution fonctionnelle.
  documentationForAI: {
    description: "Bouton de paiement configurable (Stripe, PayPal ou lien externe) pour paiement unique ou abonnement.",
    usage: "Choisir le provider et le mode, renseigner montant/devise ou planId, ou un lien externe. Personnaliser le style et les attributs data-*.",
    configSchema: {
      id: "Identifiant unique",
      title: "Titre affiché en modale",
      description: "Description pour le paiement",
      ownerId: "ID du créateur (récupération des clés Stripe/PayPal)",
      provider: "'stripe' | 'paypal' | 'link'",
      mode: "'payment' | 'subscription'",
      amount: "Montant (ex: 12.99) pour mode=payment",
      currency: "Devise (EUR/USD/GBP)",
      planId: "Identifiant d'abonnement (Stripe price ou Plan ID PayPal)",
      linkUrl: "URL du paiement/abonnement si provider='link'",
      openIn: "'modal' | 'newtab' | 'same' (pour provider=link)",
      successUrl: "URL de succès (réservé)",
      cancelUrl: "URL d'annulation (réservé)",
      label: "Texte du bouton",
      icon: "Icône (emoji/texte avant le label)",
      bgColor: "Couleur de fond (hex)",
      textColor: "Couleur du texte (hex)",
      borderColor: "Couleur de bordure (hex)",
      borderRadius: "Arrondi (px)",
      fontFamily: "Police CSS",
      fontSize: "Taille (px)",
      fontWeight: "Graisse (100-900)",
      paddingV: "Padding vertical (px)",
      paddingH: "Padding horizontal (px)",
      width: "Largeur (px, optionnel)",
      customClassName: "Classe CSS additionnelle",
      attributes: "Array de { name, value } pour data-*"
    },
    exampleConfig: {
      id: "payment-btn-1",
      title: "Paiement",
      description: "Acompte de réservation",
      provider: "stripe",
      mode: "payment",
      amount: 50,
      currency: "EUR",
      label: "Payer l'acompte",
      icon: "💳",
      bgColor: "#0d6efd",
      textColor: "#ffffff"
    }
  },

  defaultConfig,
  ConfigComponent: PaymentButtonsConfigComponent,
  PreviewComponent: PaymentButtonsPreview,

  // ✅ CORRECTION MAJEURE : ID UNIQUE
  generateHTML: (cfg: PaymentButtonsConfig) => {
    // 1. Génération de l'ID unique (éviter d'utiliser "payment-buttons" qui est le défaut)
    const instanceId = (cfg.id && cfg.id !== 'payment-buttons') 
      ? cfg.id 
      : `payment-${Date.now()}-${Math.floor(Math.random() * 1000)}`;
    
    // 2. Persistance
    const configWithId = { ...defaultConfig, ...cfg, id: instanceId };

    const desc = configWithId.mode === 'subscription' 
      ? `Abonnement ${configWithId.label}` 
      : `Paiement ${configWithId.amount} ${configWithId.currency}`;

    return generateUniformPlaceholder({
      pluginId: instanceId, // ✅ Cible unique pour l'édition
      pluginType: 'payment-buttons-modular',
      
      title: configWithId.title || 'Bouton Pay',
      description: configWithId.description || desc,
      icon: '💳',
      category: 'commerce',
      config: { ...defaultConfig, ...(cfg || {}) }
    });
  }
};

export default PaymentButtonsModular;
